import React, { useState, useEffect } from "react";
import { TextField, Button, CircularProgress, Autocomplete } from "@mui/material";
import ApiService from "../../../service/ApiService";
import { apiPaths } from "../../../service/apiPaths";
import toast from "react-hot-toast";

interface EditCronSettingsProps {
    cronId: string;
    onClose: () => void;
    onSuccess: () => void;
}

const EditCronSettings: React.FC<EditCronSettingsProps> = ({
    cronId,
    onClose,
    onSuccess,
}) => {
    const [loading, setLoading] = useState(false);
    const [questions, setQuestions] = useState<Array<{ _id: string; questionText: string }>>([]);
    const [subjectId, setSubjectId] = useState<string>("");
    const [formData, setFormData] = useState({
        end: 0,
        time: "",
        question_id: "",
    });

    // Convert 12-hour format (10:00 AM) to 24-hour format (10:00) for input
    const convertTo24Hour = (time12: string): string => {
        if (!time12) return "";
        const [time, period] = time12.split(" ");
        let [hours, minutes] = time.split(":");

        if (period === "PM" && hours !== "12") {
            hours = String(parseInt(hours) + 12);
        } else if (period === "AM" && hours === "12") {
            hours = "00";
        }

        return `${hours.padStart(2, "0")}:${minutes}`;
    };

    // Convert 24-hour format (22:00) to 12-hour format (10:00 PM) for saving
    const convertTo12Hour = (time24: string): string => {
        if (!time24) return "";
        const [hours, minutes] = time24.split(":");
        const hour = parseInt(hours);
        const period = hour >= 12 ? "PM" : "AM";
        const hour12 = hour % 12 || 12;

        return `${hour12}:${minutes} ${period}`;
    };

    useEffect(() => {
        fetchCronData();
    }, [cronId]);

    useEffect(() => {
        if (subjectId) {
            fetchQuestions();
        }
    }, [subjectId]);

    const fetchCronData = async () => {
        setLoading(true);
        try {
            const result = await ApiService({
                method: "GET",
                endpoint: `${apiPaths.getCronSettingById}/${cronId}`,
            });

            const data = result.data || result;
            setFormData({
                end: data.end || 0,
                time: data.time ? convertTo24Hour(data.time) : "",
                question_id: data.question_id?._id || "",
            });

            // Extract subject_id from the response
            if (data.subject_id?._id) {
                setSubjectId(data.subject_id._id);
            }
        } catch (error) {
            console.error("Error fetching cron data:", error);
            toast.error("Failed to fetch cron settings");
        } finally {
            setLoading(false);
        }
    };

    const fetchQuestions = async () => {
        try {
            const result = await ApiService({
                method: "GET",
                endpoint: `${apiPaths.question}/subject/${subjectId}`,
            });

            const data = result.data || [];
            setQuestions(data);
        } catch (error) {
            console.error("Error fetching questions:", error);
            toast.error("Failed to fetch questions");
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);

        try {
            // Convert time to 12-hour format before sending
            const dataToSend = {
                end: formData.end,
                time: convertTo12Hour(formData.time),
                question_id: formData.question_id,
            };

            await ApiService({
                method: "PUT",
                endpoint: `${apiPaths.updateCronSetting}/${cronId}`,
                data: dataToSend,
            });

            toast.success("Cron settings updated successfully");
            onSuccess();
            onClose();
        } catch (error) {
            console.error("Error updating cron settings:", error);
            toast.error("Failed to update cron settings");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
                <h2 className="text-2xl font-bold mb-4">Edit Cron Settings</h2>

                {loading && !formData.end ? (
                    <div className="flex justify-center py-8">
                        <CircularProgress />
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <TextField
                            fullWidth
                            label="Total Questions"
                            type="number"
                            value={formData.end}
                            onChange={(e) =>
                                setFormData({ ...formData, end: parseInt(e.target.value) || 0 })
                            }
                            required
                        />

                        <Autocomplete
                            fullWidth
                            options={questions}
                            getOptionLabel={(option) => option.questionText || ""}
                            value={questions.find(q => q._id === formData.question_id) || null}
                            onChange={(event, newValue) => {
                                setFormData({ ...formData, question_id: newValue?._id || "" });
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Select Question (Optional)"
                                    placeholder="Search questions..."
                                />
                            )}
                            noOptionsText="No questions available"
                            isOptionEqualToValue={(option, value) => option._id === value._id}
                        />

                        <TextField
                            fullWidth
                            label="Time"
                            type="time"
                            value={formData.time}
                            onChange={(e) =>
                                setFormData({ ...formData, time: e.target.value })
                            }
                            InputLabelProps={{
                                shrink: true,
                            }}
                            required
                        />

                        <div className="flex justify-end gap-3 mt-6">
                            <Button
                                onClick={onClose}
                                variant="outlined"
                                disabled={loading}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant="contained"
                                disabled={loading}
                            >
                                {loading ? <CircularProgress size={24} /> : "Save"}
                            </Button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

export default EditCronSettings;
